import {
  Button,
  Fab,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material'
import { MouseEvent, useCallback, useState } from 'react'
import { useCopyToClipboard } from 'usehooks-ts'
import { IconButton, Icons, InformationModal, SkeletonText } from 'utoniq-ui'

import {
  ConsolePreSaleDetailFragment,
  ConsolePreSaleItemSelectBoxFragment,
  Item,
  Maybe,
  PreSale,
  PreSaleType,
} from '@/clients/utoniq-core/schema'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { useBrowserLink } from '@/components/lib/BrowserLink'
import { TicketStatusCell } from '@/components/pages/ConsoleEventDetail/template/components'
import { ArtistThemeProvider, styles } from '@/components/styles'
import { useMessage, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { generateFullPath } from '@/lib/url'

import {
  ItemSelectModal,
  PreSaleApplicantsCell,
  PreSaleContentCell,
  PreSaleContentSkeletonCell,
  RewardItemsCell,
  StatusCell,
} from './components'

export * from './fragments'

export type State = {
  artistId: string
  preSaleId: string
  loading: boolean
  preSale: Maybe<ConsolePreSaleDetailFragment>
  removingPreSale: boolean
  addingItem: boolean
  itemSelectProps: {
    loading: boolean
    hasNextPage: boolean
    items: Maybe<{ edges: { node: ConsolePreSaleItemSelectBoxFragment }[] }>
  }
  items: Maybe<Item[]>
  updatedAt?: string
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onRemovePreSale: () => Promise<void>
  onRemove: (itemId: string) => Promise<void>
  onSelect: (itemIds: string[]) => Promise<void>
  onFetchMore: () => Promise<void>
  onSearch: (values: { keyword: string }) => Promise<void>
}

export const ConsolePreSaleDetailTemplate: Template<State, Action> = ({
  state,
  onRemovePreSale,
  onRemove,
  onFetchMore,
  onSelect,
  onSearch,
}) => {
  const { t } = useTranslation('consolePreSaleDetail')
  const { routerPages, router, generatePath } = useRouter()
  const { showMessage } = useMessage()
  const [_, copy] = useCopyToClipboard()
  const url =
    state.preSale?.type === PreSaleType.Lottery
      ? generateFullPath(routerPages.LotteryDetail, {
          preSaleId: state.preSaleId,
        })
      : generateFullPath(routerPages.FirstComeFirstServedDetail, {
          preSaleId: state.preSaleId,
        })
  const { onClick: onClickBrowserLink } = useBrowserLink(
    generatePath(
      state.preSale?.type === PreSaleType.Lottery
        ? routerPages.LotteryDetail
        : routerPages.FirstComeFirstServedDetail,
      { preSaleId: state.preSaleId }
    )
  )
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [openTrashConfirm, setOpenTrashConfirm] = useState<boolean>(false)

  const options = [
    {
      label: t('applicants'),
      async onClick() {
        router.push(
          generatePath(routerPages.ConsoleLotteryManagements, {
            preSaleId: state.preSaleId,
          })
        )
      },
      hidden: state.preSale?.type !== PreSaleType.Lottery,
    },

    {
      label: t('openPreSaleLabel'),
      icon: (
        <ListItemIcon sx={{ '&.MuiListItemIcon-root': { minWidth: 24 } }}>
          <Icons.Window fontSize="xSmall" />
        </ListItemIcon>
      ),
      onClick() {
        onClickBrowserLink()
        setAnchorEl(null)
      },
    },
    {
      label: t('copyURLLabel'),
      async onClick() {
        await copy(url)
        showMessage(t('copyURLSuccess'), 'success')
        setAnchorEl(null)
      },
    },
    {
      label: t('editLabel'),
      onClick() {
        router.push(
          generatePath(routerPages.ConsolePreSaleEdit, {
            preSaleId: state.preSaleId,

            eventId: state.preSale?.event.id ?? '#',
          })
        )
        handleClose()
      },
    },
    /**
     * TODO: 先行販売削除のAPI作成後に削除ボタンを実装
     */
    // {
    //   label: t('removeLabel'),
    //   onClick() {
    //     handleClose()
    //     setOpenTrashConfirm(true)
    //   },
    // },
  ]

  const handleClick = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <ConsoleBaseLayout
        margin
        header={{
          back: true,
          defaultBackPage: state.preSale?.event?.id
            ? generatePath(routerPages.ConsoleShokufesEventDetail, {
                eventId: state.preSale?.event.id ?? '#',
              })
            : routerPages.ConsoleDashboard,
          title: state.preSale?.title,
          right: (
            <IconButton.More
              edge="end"
              size="medium"
              onClick={handleClick}
              color="white"
            />
          ),
        }}
      >
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          {options.map(option => (
            <MenuItem
              key={option.label}
              onClick={option.onClick}
              sx={{ width: 200 }}
              hidden={option.hidden}
            >
              {option.icon}
              {option.label}
            </MenuItem>
          ))}
        </Menu>
        {state.loading || !state.preSale ? (
          <>
            <PreSalTitleCellSkeleton />
            <PreSaleContentSkeletonCell />
          </>
        ) : (
          <>
            <PreSalTitleCell title={state.preSale.title} />
            <StatusCell
              isPublished={state.preSale.isPublished}
              isLottery={!!state.preSale.lottery}
              preSaleStatus={state.preSale.preSaleStatus}
              createdAt={state.preSale.formattedEndedAt}
            />
            <PreSaleContentCell preSale={state.preSale} />
            <PreSaleApplicantsCell preSale={state.preSale} />
            <BaseCell
              artistId={state.artistId}
              addingItem={state.addingItem}
              itemSelectProps={state.itemSelectProps}
              values={state.preSale?.itemIds ?? []}
              onFetchMore={onFetchMore}
              onSelect={onSelect}
              onSearch={onSearch}
            />
            {!!state.preSale?.preSalesAggregation && (
              <TicketStatusCell
                preSalesAggregation={state.preSale.preSalesAggregation}
                updatedAt={state.updatedAt}
              />
            )}
          </>
        )}
        <RewardItemsCell
          onRemove={onRemove}
          loading={state.loading}
          items={state.preSale?.items}
        />
        <InformationModal
          open={openTrashConfirm}
          onClose={() => setOpenTrashConfirm(false)}
          title={t('trashConfirmTitle')}
          content={t('trashConfirmDesc')}
          cancelText={t('cancel')}
          buttons={
            <Button
              sx={{ minWidth: 212 }}
              variant="contained"
              color="primary"
              size="large"
              loading={state.removingPreSale}
              onClick={async () => {
                await onRemovePreSale()
                setOpenTrashConfirm(false)
              }}
            >
              {t('delete')}
            </Button>
          }
        />
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}

type PreSalTitleCellProps = {
  title: PreSale['title']
}
const PreSalTitleCell: Cell<PreSalTitleCellProps> = ({ title }) => {
  return (
    <CellFrame sx={{ pt: 5 }}>
      <Typography variant="h4">{title}</Typography>
    </CellFrame>
  )
}

const PreSalTitleCellSkeleton: Cell = () => {
  return (
    <CellFrame sx={{ pt: 5 }}>
      <SkeletonText variant="large" width="75%" />
    </CellFrame>
  )
}

type BaseCellProps = {
  artistId: State['artistId']
  values: string[]
  addingItem: boolean
  itemSelectProps: State['itemSelectProps']
  onFetchMore: Action['onFetchMore']
  onSelect: Action['onSelect']
  onSearch: Action['onSearch']
}
const BaseCell: Cell<BaseCellProps> = ({
  artistId,
  values,
  addingItem,
  itemSelectProps,
  onFetchMore,
  onSelect,
  onSearch,
}) => {
  const [open, setOpen] = useState<boolean>(false)

  const toggleDrawer = useCallback((open: boolean) => {
    setOpen(open)
  }, [])

  return (
    <Stack direction="row-reverse">
      <Stack
        sx={{
          position: 'fixed',
          bottom: 16,
          zIndex: 5,
          maxWidth: 600,
          margin: '0 auto',
          px: 4,
          pb: styles.safeAreaBottom,
        }}
      >
        <Fab
          size="medium"
          color="primary"
          onClick={() => {
            toggleDrawer(true)
            onSearch({ keyword: '' })
          }}
        >
          <Icons.ThinPlus fontSize="medium" sx={{ color: 'white' }} />
        </Fab>
      </Stack>
      <ItemSelectModal
        {...itemSelectProps}
        values={values}
        artistId={artistId}
        open={open}
        onSearch={onSearch}
        addingItem={addingItem}
        onFetchMore={onFetchMore}
        onSelect={onSelect}
        onClose={() => toggleDrawer(false)}
      />
    </Stack>
  )
}
